import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store';

/** Routes usable before the setup wizard is finished. */
const SETUP_EXEMPT_PATHS = ['/setup', '/help', '/profile'];

function isSetupExempt(pathname: string): boolean {
  return SETUP_EXEMPT_PATHS.includes(pathname) || pathname.startsWith('/setup/');
}

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const totpRequired = useAuthStore((state) => state.totpRequired);
  const user = useAuthStore((state) => state.user);
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to={`/signin?from=${encodeURIComponent(location.pathname)}`} replace />;
  }

  if (totpRequired) {
    return <Navigate to="/verify-totp" replace />;
  }

  // 2FA enrollment is mandatory and happens exactly once — right after the
  // user first signs in post-verification. Funnelling it here (rather than a
  // lazy gate on enrichment/purchase) is what fixes B-03: the prompt never
  // appears mid-flow at the moment of purchase.
  if (user && user.totp_enabled === false) {
    return <Navigate to="/setup-2fa" replace />;
  }

  // Lock all routes except /setup, help and profile until setup is complete
  // (T-242: support must be reachable while a new user is still onboarding).
  if (!user?.setupComplete && !isSetupExempt(location.pathname)) {
    return <Navigate to="/setup" replace />;
  }

  return <>{children}</>;
}
